import { Routes } from '@angular/router';
import { authGuard, anonymousGuard } from './core/guards/auth.guard';
import { profileViewGuard, profileEditGuard } from './core/guards/profile.guard';
import {
  accommodationViewGuard,
  accommodationEditGuard,
  availabilityGuard,
  photosGuard,
} from './core/guards/accommodation.guard';
import { collaboratorGuard } from './core/guards/collaborator.guard';
import { managerGuard } from './core/guards/manager.guard';
import { walletGuard } from './core/guards/wallet.guard';
import { referralGuard } from './core/guards/referral.guard';
import { helpGuard } from './core/guards/help.guard';
import { availabilityRequestsGuard } from './core/guards/availability-requests.guard';
import { collaboratorsGuard } from './core/guards/collaborators.guard';
import { browseGuard } from './core/guards/browse.guard';
import { bookingsGuard } from './core/guards/booking.guard';
import { salesGuard } from './core/guards/sales.guard';

export const routes: Routes = [
  // Landing — public entry point
  {
    path: '',
    pathMatch: 'full',
    canActivate: [anonymousGuard],
    loadComponent: () =>
      import('./features/public/landing/landing').then((m) => m.Landing),
  },

  // Public auth routes (login, register, password recovery) — share the same layout
  {
    path: '',
    loadComponent: () =>
      import('./features/public/public-layout/public-layout').then((m) => m.PublicLayout),
    children: [
      {
        path: 'login',
        canActivate: [anonymousGuard],
        loadComponent: () =>
          import('./features/public/login/login').then((m) => m.Login),
      },
      {
        path: 'register',
        canActivate: [anonymousGuard],
        loadComponent: () =>
          import('./features/public/register/register').then((m) => m.Register),
      },
      {
        path: 'forgot-password',
        canActivate: [anonymousGuard],
        loadComponent: () =>
          import('./features/public/forgot-password/forgot-password').then(
            (m) => m.ForgotPassword,
          ),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('./features/public/reset-password/reset-password').then(
            (m) => m.ResetPassword,
          ),
      },
      {
        // Unified Firebase Auth email-action handler (verifyEmail, recoverEmail, …).
        // resetPassword links are delegated to /reset-password by the component.
        path: 'auth/action',
        loadComponent: () =>
          import('./features/public/auth-action/auth-action').then((m) => m.AuthAction),
      },
      {
        // Collaborator invitation (T3). Deliberately WITHOUT anonymousGuard: that
        // guard bounces any signed-in user to their own area, so a socio who clicked
        // a collaborator link would land on /home with no explanation. The component
        // branches instead and asks them to sign out, because accepting creates a
        // NEW account. Same reasoning as reset-password and auth/action above.
        //
        // First public route in the app with a path param; the SPA rewrite in
        // firebase.json ("**" → /index.html) already covers it.
        path: 'invite/:token',
        loadComponent: () =>
          import('./features/public/collaborator-invite/collaborator-invite').then(
            (m) => m.CollaboratorInvite,
          ),
      },
    ],
  },

  // Terms (standalone pages, no layout)
  {
    path: 'terms',
    loadComponent: () =>
      import('./features/public/terms/terms').then((m) => m.Terms),
  },
  {
    // Términos del programa de colaboradores (T9). Documento propio y no una sección
    // de `/terms`: el invitado tiene que poder leer lo que acepta sin pasar por un
    // texto escrito en el vocabulario del socio (TravelCoins, billetera, comisiones),
    // que es justo lo que `localization.md` §7 prohíbe mostrarle.
    path: 'terms/colaborador',
    loadComponent: () =>
      import('./features/public/collaborator-terms/collaborator-terms').then(
        (m) => m.CollaboratorTerms,
      ),
  },

  // Collaborator area (T3, grown into a layout with children in T6). Top-level
  // rather than a child of AuthenticatedLayout, whose sidebar is built around the
  // socio (profile, accommodation, wallet, referrals). ROLE_AREA_ROUTE points every
  // collaborator redirect at `/collaborator`, and the URL did not have to move when
  // T6 added the children.
  //
  // The catalog is the SAME `Browse` component the socio uses, mounted here instead
  // of reusing the `/browse` route: BrowseLayout's non-manager sidebar IS the socio
  // sidebar, and browseGuard also protects `/bookings/:id`, which is denominated in
  // TravelCoins. Both would have leaked into the collaborator's area. The currency
  // switch is AmountDisplayService. See docs/wiki/angular-routing-and-guards.md §3.8.
  {
    path: 'collaborator',
    canActivate: [collaboratorGuard],
    loadComponent: () =>
      import('./features/collaborator/collaborator-layout/collaborator-layout').then(
        (m) => m.CollaboratorLayout,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/collaborator/collaborator-home/collaborator-home').then(
            (m) => m.CollaboratorHome,
          ),
      },
      {
        path: 'catalogo',
        loadComponent: () => import('./features/browse/browse').then((m) => m.Browse),
        data: {
          heading: 'Buscar alojamientos',
          subheading: 'Elige fechas y usa tu beneficio',
        },
      },
      {
        path: 'reservas',
        loadComponent: () =>
          import('./features/collaborator/collaborator-bookings/collaborator-bookings').then(
            (m) => m.CollaboratorBookings,
          ),
      },
    ],
  },

  // Manager routes
  {
    path: 'manager',
    canActivate: [managerGuard],
    loadComponent: () =>
      import('./features/manager/manager-layout/manager-layout').then(
        (m) => m.ManagerLayout,
      ),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/manager/dashboard/manager-dashboard').then(
            (m) => m.ManagerDashboard,
          ),
      },
      {
        path: 'members/:uid',
        loadComponent: () =>
          import('./features/manager/member-detail/member-detail').then(
            (m) => m.MemberDetailComponent,
          ),
      },
      {
        path: 'config',
        loadComponent: () =>
          import('./features/manager/system-config/system-config').then(
            (m) => m.SystemConfig,
          ),
      },
      {
        path: 'delete-member',
        loadComponent: () =>
          import('./features/manager/delete-member/delete-member').then(
            (m) => m.DeleteMember,
          ),
      },
      {
        path: 'wallet',
        loadComponent: () =>
          import('./features/manager/wallet/manager-wallet').then(
            (m) => m.ManagerWallet,
          ),
      },
      {
        path: 'bookings',
        loadComponent: () =>
          import('./features/manager/bookings/manager-bookings').then(
            (m) => m.ManagerBookings,
          ),
      },
      {
        path: 'availability-requests',
        loadComponent: () =>
          import(
            './features/manager/availability-requests/manager-availability-requests'
          ).then((m) => m.ManagerAvailabilityRequests),
      },
      {
        path: 'payments',
        loadComponent: () =>
          import('./features/manager/payments/manager-payments').then(
            (m) => m.ManagerPayments,
          ),
      },
      {
        path: 'notifications',
        loadComponent: () =>
          import('./features/manager/notifications/notifications-admin').then(
            (m) => m.NotificationsAdminPage,
          ),
      },
      {
        path: 'sales-calls',
        loadComponent: () =>
          import('./features/manager/sales-calls/manager-sales-calls').then(
            (m) => m.ManagerSalesCalls,
          ),
      },
      {
        path: 'sales-calls/:id',
        loadComponent: () =>
          import('./features/sales/call-detail/call-detail').then(
            (m) => m.SalesCallDetailComponent,
          ),
      },
      {
        path: 'lead-agent',
        loadComponent: () =>
          import('./features/manager/lead-agent/lead-agent').then((m) => m.LeadAgent),
      },
      {
        path: 'campaigns',
        loadComponent: () =>
          import('./features/manager/campaigns/manager-campaigns').then(
            (m) => m.ManagerCampaigns,
          ),
      },
      {
        path: 'campaigns/:id',
        loadComponent: () =>
          import('./features/manager/campaigns/campaign-detail').then(
            (m) => m.CampaignDetailPage,
          ),
      },
      {
        path: 'lead-emails',
        loadComponent: () =>
          import('./features/manager/lead-emails/manager-lead-emails').then(
            (m) => m.ManagerLeadEmails,
          ),
      },
    ],
  },

  // Sales routes (rol "ventas")
  {
    path: 'sales',
    canActivate: [salesGuard],
    loadComponent: () =>
      import('./features/sales/sales-layout/sales-layout').then((m) => m.SalesLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/sales/leads/sales-leads').then((m) => m.SalesLeads),
      },
      {
        path: 'leads/:id',
        loadComponent: () =>
          import('./features/sales/leads/lead-detail').then((m) => m.LeadDetail),
      },
      {
        path: 'calls',
        loadComponent: () =>
          import('./features/sales/sales-dashboard/sales-dashboard').then(
            (m) => m.SalesDashboard,
          ),
      },
      {
        path: 'calls/:id',
        loadComponent: () =>
          import('./features/sales/call-detail/call-detail').then(
            (m) => m.SalesCallDetailComponent,
          ),
      },
      {
        path: 'invitations',
        loadComponent: () =>
          import('./features/sales/invitations/sales-invitations').then(
            (m) => m.SalesInvitations,
          ),
      },
      {
        path: 'registering-members',
        loadComponent: () =>
          import('./features/sales/registering-members/registering-members').then(
            (m) => m.RegisteringMembers,
          ),
      },
      {
        path: 'linphone',
        loadComponent: () =>
          import('./features/sales/linphone/linphone-config').then(
            (m) => m.LinphoneConfig,
          ),
      },
    ],
  },

  // Authenticated routes (socio)
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/authenticated/authenticated-layout/authenticated-layout').then(
        (m) => m.AuthenticatedLayout,
      ),
    children: [
      {
        path: 'home',
        loadComponent: () =>
          import('./features/authenticated/home/home').then((m) => m.Home),
      },
      {
        path: 'profile',
        canActivate: [profileViewGuard],
        loadComponent: () =>
          import('./features/authenticated/profile/profile-view/profile-view').then(
            (m) => m.ProfileView,
          ),
      },
      {
        path: 'profile/edit',
        canActivate: [profileEditGuard],
        loadComponent: () =>
          import('./features/authenticated/profile/profile-edit/profile-edit').then(
            (m) => m.ProfileEdit,
          ),
      },
      {
        path: 'accommodation',
        canActivate: [accommodationViewGuard],
        loadComponent: () =>
          import(
            './features/authenticated/accommodation/accommodation-view/accommodation-view'
          ).then((m) => m.AccommodationView),
      },
      {
        path: 'accommodation/edit',
        canActivate: [accommodationEditGuard],
        loadComponent: () =>
          import(
            './features/authenticated/accommodation/accommodation-edit/accommodation-edit'
          ).then((m) => m.AccommodationEdit),
      },
      {
        path: 'accommodation/availability',
        canActivate: [availabilityGuard],
        loadComponent: () =>
          import(
            './features/authenticated/accommodation/accommodation-availability/accommodation-availability'
          ).then((m) => m.AccommodationAvailability),
      },
      {
        path: 'accommodation/photos',
        canActivate: [photosGuard],
        loadComponent: () =>
          import(
            './features/authenticated/accommodation/accommodation-photos/accommodation-photos'
          ).then((m) => m.AccommodationPhotos),
      },
      {
        path: 'wallet',
        canActivate: [walletGuard],
        loadComponent: () =>
          import('./features/authenticated/wallet/wallet').then((m) => m.WalletView),
      },
      {
        path: 'wallet/top-up',
        canActivate: [walletGuard],
        loadComponent: () =>
          import('./features/authenticated/wallet/top-up/wallet-top-up').then(
            (m) => m.WalletTopUp,
          ),
      },
      {
        path: 'wallet/return',
        canActivate: [walletGuard],
        loadComponent: () =>
          import('./features/authenticated/wallet/return/wallet-return').then(
            (m) => m.WalletReturn,
          ),
      },
      {
        path: 'referrals',
        canActivate: [referralGuard],
        loadComponent: () =>
          import('./features/authenticated/referral/referral').then((m) => m.Referral),
      },
      {
        path: 'bookings',
        canActivate: [bookingsGuard],
        loadComponent: () =>
          import('./features/authenticated/bookings/bookings').then((m) => m.Bookings),
      },
      {
        path: 'availability-requests',
        canActivate: [availabilityRequestsGuard],
        loadComponent: () =>
          import(
            './features/authenticated/availability-requests/availability-requests'
          ).then((m) => m.AvailabilityRequests),
      },
      // Nómina del socio empleador. Plural a propósito: `/collaborator`
      // (singular, más arriba) es el área del colaborador mismo.
      {
        path: 'collaborators',
        canActivate: [collaboratorsGuard],
        loadComponent: () =>
          import('./features/authenticated/collaborators/collaborators').then(
            (m) => m.Collaborators,
          ),
      },
      {
        path: 'collaborators/:id',
        canActivate: [collaboratorsGuard],
        loadComponent: () =>
          import('./features/authenticated/collaborators/collaborator-detail').then(
            (m) => m.CollaboratorDetail,
          ),
      },
      {
        path: 'help',
        canActivate: [helpGuard],
        loadComponent: () =>
          import('./features/authenticated/help/help').then((m) => m.Help),
      },
    ],
  },

  // Browse route — accessible to both managers and approved socios
  {
    path: 'browse',
    canActivate: [browseGuard],
    loadComponent: () =>
      import('./features/browse/browse-layout').then((m) => m.BrowseLayout),
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./features/browse/browse').then((m) => m.Browse),
      },
    ],
  },

  // Booking detail — accessible to both managers and approved socios (participant check done by API)
  {
    path: 'bookings',
    canActivate: [browseGuard],
    loadComponent: () =>
      import('./features/browse/browse-layout').then((m) => m.BrowseLayout),
    children: [
      {
        path: ':id',
        loadComponent: () =>
          import('./features/authenticated/bookings/booking-detail').then(
            (m) => m.BookingDetail,
          ),
      },
    ],
  },

  // Fallback
  { path: '**', redirectTo: '' },
];
